import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useForm, FormProvider } from 'react-hook-form';
import { EditAccountInfoModal } from '@/components/EditAccountInfoModal/EditAccountInfoModal';
import {
  CurrentPasswordSection,
  ErrorObject,
  PasswordError,
} from '@/components/EditAccountInfoModal/components';
import { Input } from '@/theme/index';
import { request } from '@/utils/requestHelpers';
import { schemaEditNameForm } from '@/utils/schemaForms/schemaForms';

/**
 * This component renders an EditAccountInfoModal (see ./EditAccountInfoModal) with the purpose of editing the
 * user's name. It renders an input for the user's first name, last name, and current password, and updates the name
 * to any string. Because password validation is performed server-side, all passwords will work in this context.
 */
export function EditNameModal({
  firstName,
  lastName,
  isOpen,
  onCloseHandle,
  onSubmit,
}: EditNameModalProps) {
  const methods = useForm({
    resolver: yupResolver(schemaEditNameForm),
    defaultValues: {
      newFirstName: firstName,
      newLastName: lastName,
      password: '',
    },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
  } = methods;

  const query = useMutation({
    mutationFn: accountUpdate,
    onSuccess: (_mutationResponse, variables) => {
      onSubmit(variables.newFirstName, variables.newLastName);
    },
    onError: (error) => {
      const err = error as ErrorObject;
      setError('password', {
        type: 'custom',
        message: err.error || err.message || 'invalid',
      });
    },
  });

  async function submit({ password, newFirstName, newLastName }: formProps) {
    query.mutate({ password, newFirstName, newLastName });
  }

  return (
    <FormProvider {...methods}>
      <EditAccountInfoModal
        isOpen={isOpen}
        onCloseHandle={onCloseHandle}
        onSubmit={handleSubmit(submit)}
        headerText="Edit your name"
        buttonText="Update name"
        dataCy="edit_name"
      >
        <PasswordError
          showCurrentPasswordError={!!errors.password}
          message={errors.password?.message}
        />
        <Input
          {...register('newFirstName')}
          sx={{ marginBottom: '10px' }}
          fullWidth
          id="first-name-input"
          type="text"
          error={!!errors.newFirstName}
          helperText={errors.newFirstName?.message}
          placeholder="First name"
          inputProps={{ 'data-cy': 'edit_name_fn' }}
        />
        <Input
          {...register('newLastName')}
          fullWidth
          id="last-name-input"
          type="text"
          error={!!errors.newLastName}
          helperText={errors.newLastName?.message}
          placeholder="Last name"
          inputProps={{ 'data-cy': 'edit_name_ln' }}
        />
        <CurrentPasswordSection />
      </EditAccountInfoModal>
    </FormProvider>
  );
}

interface EditNameModalProps {
  /** The user's original first name, most likely passed from an account object. */
  firstName: string;
  /** The user's original last name, most likely passed from an account object. */
  lastName: string;
  /** If true, the modal is open */
  isOpen: boolean;
  /** Callback fired when the component requests to be closed. The reason parameter can optionally be used to control the response to onClose */
  onCloseHandle: () => void;
  /** Callback fired when the user submits the form. */
  onSubmit: (newFirstName: string, newLastName: string) => void;
}

interface formProps {
  password: string;
  newFirstName: string;
  newLastName: string;
}

async function accountUpdate({
  password,
  newFirstName,
  newLastName,
}: formProps): Promise<void> {
  const params = {
    account: {
      first_name: newFirstName,
      last_name: newLastName,
    },
    current_password: password,
  };

  await request(
    '/api/v5/account',
    {
      headers: new Headers({
        'content-type': 'application/json',
      }),
      method: 'PATCH',
      body: JSON.stringify(params),
    },
    'account'
  );
}
